import { useState } from "react";

import { MenuVerticalFilled, UserFilled } from "@aircall/icons";
import {
  Avatar,
  AvatarIcon,
  AvatarText,
  Box,
  Button,
  Dropdown,
  Flex,
  Menu,
  MenuItem,
  Spacer,
  Textarea,
  TextareaProps,
  Typography,
} from "@aircall/tractor";
import { getInitials } from "@dashboard/library";
import { format } from "date-fns";
import { useTranslation } from "react-i18next";

import { useDeleteNote } from "@/hooks/useDeleteNote/useDeleteNote";
import { useUpdateNote } from "@/hooks/useUpdateNote/useUpdateNote";

import { useCurrentUser } from "@/hooks/useCurrentUser/useCurrentUser";
import {
  NoteActionsProps,
  NotesListItemProps,
  UserAvatarProps,
} from "./NotesListItem.decl";

function UserAvatar({ firstName, lastName }: UserAvatarProps) {
  if (!firstName || !lastName) {
    return (
      <Avatar size="small" variant="default">
        <AvatarIcon icon={UserFilled} />
      </Avatar>
    );
  }

  return (
    <Avatar size="small" variant="default">
      <AvatarText initials={getInitials(firstName, lastName)} />
    </Avatar>
  );
}

function NoteActions({ show, id, onDelete, onEdit }: NoteActionsProps) {
  const { t } = useTranslation();

  if (!show) {
    return <Box w="28px" h="28px" />;
  }

  return (
    <Dropdown
      data-test={`notes-list-item-actions-${id}`}
      align="end"
      side="bottom"
      trigger={
        <Button
          data-test={`notes-list-item-button-${id}`}
          variant="secondary"
          mode="ghost"
          size="xSmall"
          shape="circle"
        >
          <MenuVerticalFilled />
        </Button>
      }
    >
      <Menu>
        <MenuItem data-test="notes-list-item-actions-edit" onClick={onEdit}>
          {t("edit")}
        </MenuItem>
        <MenuItem data-test="notes-list-item-actions-delete" onClick={onDelete}>
          {t("delete")}
        </MenuItem>
      </Menu>
    </Dropdown>
  );
}

export function NotesListItem({
  id,
  content,
  createdAt,
  firstName,
  lastName,
  userId,
  isLoadingMutation,
}: NotesListItemProps) {
  const { t } = useTranslation();
  const [showTextarea, setShowTextarea] = useState(false);
  const [value, setValue] = useState(content);
  const { deleteNote } = useDeleteNote();
  const { updateNote, loading: isLoadingUpdate } = useUpdateNote();
  const { currentUser } = useCurrentUser();

  const loading = isLoadingUpdate || isLoadingMutation;

  const fullName = firstName && lastName ? `${firstName} ${lastName}` : "";
  const isNoteOwner = userId === Number(currentUser?.ID);

  const showActions = !loading && isNoteOwner;

  const handleCancelOnClick = () => {
    setShowTextarea(false);
    setValue(content);
  };
  const handleOnEdit = () => {
    setShowTextarea(true);
  };
  const handleOnDelete = () => {
    deleteNote({
      noteId: id,
    });
  };
  const handleOnChange: TextareaProps["onChange"] = (e) => {
    setValue(e.target.value);
  };
  const handleUpdateOnClick = () => {
    updateNote({
      noteId: id,
      content: value,
    });
    setShowTextarea(false);
  };
  const handleOnClear = () => {
    setValue("");
  };

  if (showTextarea) {
    return (
      <Spacer direction="vertical" space="s" fluid>
        <Textarea
          onChange={handleOnChange}
          onClear={value ? handleOnClear : undefined}
          value={value}
        />
        <Spacer space="xxs" justifyContent="flex-end" w="100%">
          <Button
            onClick={handleCancelOnClick}
            data-test="notes-list-item-actions-cancel"
            size="small"
            variant="secondary"
            mode="ghost"
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleUpdateOnClick}
            data-test="notes-list-item-actions-done"
            size="small"
          >
            {t("done")}
          </Button>
        </Spacer>
      </Spacer>
    );
  }

  return (
    <Spacer
      data-test={`notes-list-item-${id}`}
      direction="vertical"
      gap="xxs"
      w="100%"
    >
      <Flex justifyContent="space-between" gap="xxxs">
        <Spacer gap="s" alignItems="center">
          <Spacer gap="xxxs" alignItems="center">
            <UserAvatar firstName={firstName} lastName={lastName} />
            <Typography variant="bodyMediumS">{fullName}</Typography>
          </Spacer>
          <Typography variant="bodyRegularS" color="text-placeholder">
            {format(new Date(createdAt), "MMM d - HH:mm")}
          </Typography>
        </Spacer>
        <NoteActions
          onDelete={handleOnDelete}
          onEdit={handleOnEdit}
          show={showActions}
          id={id}
        />
      </Flex>
      <Typography variant="bodyRegularS">{content}</Typography>
    </Spacer>
  );
}
